import { ArrowRight, Search, Pen, DollarSign, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "./ScrollReveal";
import keywordResearchImg from "@/assets/keyword_research.png";
import nexisAiImg from "@/assets/nexis_ai.png";
import monetisationAiImg from "@/assets/monetisation_ai.png";
import salesPageBuilderImg from "@/assets/sales_page_builder.png";

const steps = [
  { number: "1", title: "DISCOVER", icon: Search, description: "Use Keyword Research AI to find what your audience is searching for right now. Updated daily. Type any keyword or let the AI surface the highest-opportunity topics in your niche today.", image: keywordResearchImg },
  { number: "2", title: "CREATE", icon: Pen, description: "Nexis AI writes your scripts, generates your video ideas, creates image and video prompts, and produces fully optimised YouTube titles and descriptions. Then use Clipping AI to turn your long-form content into short-form clips with captions burned in — ready for TikTok, Reels, and Shorts.", image: nexisAiImg },
  { number: "3", title: "MONETISE", icon: DollarSign, description: "Monetisation AI analyses your niche and audience, then builds your complete digital product in one click — ebook, course, template pack, or newsletter. Full outline, chapters, pricing strategy, and sales page copy included. Works whether you have 500 followers or 500,000.", image: monetisationAiImg },
  { number: "4", title: "LAUNCH", icon: Rocket, description: "Sales Page Builder AI generates your complete sales page from the product the AI just built. Email List Builder AI creates your lead magnet, opt-in page, and welcome email sequence. Your entire income system — built, written, and ready to launch.", image: salesPageBuilderImg },
];

export const FourStepsSection = () => {
  return (
    <section className="py-10 md:py-20 relative" style={{ background: "#FAFAFA" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-16">
            <h2 className="font-display font-bold mb-4 md:mb-6 leading-tight" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 40px)" }}>
              From First Follower to First Sale in{" "}
              <span style={{ color: "#F59E0B" }}>4 Steps</span>
            </h2>
          </div>
        </ScrollReveal>

        <div className="max-w-5xl mx-auto space-y-4 md:space-y-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="relative p-5 md:p-8 rounded-2xl bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ border: "1px solid #E5E7EB" }}>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex gap-4 md:gap-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(124,58,237,0.08)" }}>
                          <Icon className="w-6 h-6 md:w-7 md:h-7" style={{ color: "#7C3AED" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display text-lg md:text-xl font-bold mb-2" style={{ color: "#111827" }}>
                            Step {step.number}: {step.title}
                          </h3>
                          <p className="text-sm md:text-base leading-relaxed" style={{ color: "#4B5563" }}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="md:w-1/2 flex-shrink-0">
                      <img src={step.image} alt={`Step ${step.number}: ${step.title}`} className="w-full object-contain rounded-xl" style={{ border: "1px solid #E5E7EB" }} />
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <div className="flex justify-center mt-8 md:mt-14 px-2">
          <a href="#pricing" className="block w-full md:w-auto">
            <Button variant="hero" size="xl" className="w-full md:w-auto group text-base md:text-lg px-8 py-5 md:px-12 md:py-6 max-w-[400px] btn-interactive">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
